import { Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect } from 'react';
import { useWatch } from 'react-hook-form';

import { Card } from '@/components/layout/Card/Card';
import { FormLayoutItem, FormLayoutRow } from '@/components/layout/FormLayout';
import { FormModal } from '@/components/modals/FormModal/FormModal';
import { FormModalActions } from '@/components/modals/FormModal/FormModalActions';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { LoadingOverlay } from '@/components/progress/LoadingOverlay/LoadingOverlay';
import {
  FormProvider,
  useForm,
  useFormContext,
} from '@/components/react-hook-form';
import { useFormSaveHandler } from '@/hooks/useFormSaveHandler';
import { useReportError } from '@/hooks/useReportError';

import {
  EstateWaterfallHypotheticalCharitableTrustModalContextProvider,
  useEstateWaterfallHypotheticalCharitableTrustModalContext,
} from './EstateWaterfallHypotheticalCharitableTrustModal.context';
import {
  HypotheticalCharitableTrustFormShape,
  HypotheticalCharitableTrustModalKind,
} from './EstateWaterfallHypotheticalCharitableTrustModal.types';
import {
  getButtonText,
  getDefaultValues,
  getHypotheticalCharitableTrustModalKindLabel,
  getModalHeading,
  mapFormDataToCreateInput,
  mapFormDataToUpdateInput,
} from './EstateWaterfallHypotheticalCharitableTrustModal.utils';
import { FundingWarning } from './Form/FundingWarning';
import {
  ForceLiquidationOnTransferField,
  FundingRadioGroupField,
  SelectedAssetDetailsField,
  SimulatedProposalSelectField,
  SourceEntitySelectField,
  TransferNameField,
} from './Form/HypotheticalCharitableTrustForm.fields';
import { useEstateWaterfallHypotheticalCharitableTrustModalQuery } from './graphql/EstateWaterfallHypotheticalCharitableTrustModal.generated';
import { useMutations } from './hooks/useMutations';
import { useSyncDataToForm } from './hooks/useSyncDataToForm';

export interface EstateWaterfallHypotheticalCharitableTrustModalProps {
  isOpen: boolean;
  waterfallId: string;
  // These IDs are used for editing
  hcrtId?: string | null;
  hcltId?: string | null;
  charitableTrustModalKind: HypotheticalCharitableTrustModalKind;
  onClose: () => void;
  initialSourceId?: string;
  householdId: string;
}

function EstateWaterfallHypotheticalCharitableTrustModalSyncer({
  householdId,
  waterfallId,
}: {
  householdId: string;
  waterfallId: string;
}) {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();
  const { syncDataToForm } = useSyncDataToForm();

  const { setIsLoading: setIsContextLoading, charitableTrustModalKind } =
    useEstateWaterfallHypotheticalCharitableTrustModalContext();

  const { data, loading } =
    useEstateWaterfallHypotheticalCharitableTrustModalQuery({
      fetchPolicy: 'cache-and-network',
      variables: {
        waterfallId,
        householdId,
        isCrt:
          charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CRT,
        isClt:
          charitableTrustModalKind === HypotheticalCharitableTrustModalKind.CLT,
      },
      onError: (error) => {
        showFeedback('Failed to load hypothetical charitable trust');
        reportError('Failed to load hypothetical charitable trust', error);
      },
    });

  useEffect(() => {
    setIsContextLoading(loading);
  }, [loading, setIsContextLoading]);

  useEffect(() => {
    if (data) {
      syncDataToForm(data);
    }
  }, [data, syncDataToForm]);

  return null;
}

function EstateWaterfallHypotheticalCharitableTrustModalInner({
  isOpen,
  onClose: onCloseExternal,
  waterfallId,
  householdId,
}: EstateWaterfallHypotheticalCharitableTrustModalProps) {
  const theme = useTheme();
  const { formRef, handleSave } = useFormSaveHandler();

  const { control } = useFormContext<HypotheticalCharitableTrustFormShape>();

  const [fundingKind] = useWatch({
    control,
    name: ['fundingKind'],
  });

  const {
    charitableTrustModalKind,
    hypotheticalCharitableTrustId,
    isLoading,
    proposalOptions,
    sourceEntityOptions,
    sourceIndividualOptions,
    sourceValues,
    fundingId,
  } = useEstateWaterfallHypotheticalCharitableTrustModalContext();

  const trustKindLabel = getHypotheticalCharitableTrustModalKindLabel(
    charitableTrustModalKind
  );

  const { handleSubmit, formState, shouldBlockNavigation } =
    useFormContext<HypotheticalCharitableTrustFormShape>();

  const handleClose = useCallback(() => {
    onCloseExternal();
  }, [onCloseExternal]);

  const {
    create,
    update,
    delete: deleteMutation,
    deleteProps,
  } = useMutations(charitableTrustModalKind, handleClose);

  const onValidSubmit = (data: HypotheticalCharitableTrustFormShape) => {
    if (hypotheticalCharitableTrustId) {
      return update(
        mapFormDataToUpdateInput(
          data,
          hypotheticalCharitableTrustId,
          fundingId,
          charitableTrustModalKind,
          householdId
        )
      );
    }

    return create(
      mapFormDataToCreateInput(
        data,
        waterfallId,
        charitableTrustModalKind,
        householdId
      )
    );
  };

  const handleDelete = useCallback(() => {
    if (!hypotheticalCharitableTrustId) {
      return;
    }

    void deleteMutation([hypotheticalCharitableTrustId]);
  }, [deleteMutation, hypotheticalCharitableTrustId]);

  const onSubmit = handleSubmit(onValidSubmit);
  const isCreating = !hypotheticalCharitableTrustId;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      heading={getModalHeading({ isCreating, trustKindLabel })}
      rightHeaderContent={null}
      shouldBlockClose={shouldBlockNavigation}
      actions={
        <FormModalActions.Provider formState={formState}>
          {!isCreating && (
            <FormModalActions.DeleteButton
              loading={deleteProps.loading}
              onConfirmDelete={handleDelete}
            />
          )}
          <FormModalActions.CancelButton onClick={handleClose} />
          <FormModalActions.SaveButton onClick={handleSave}>
            {getButtonText({ isCreating })}
          </FormModalActions.SaveButton>
        </FormModalActions.Provider>
      }
    >
      <form ref={formRef} onSubmit={onSubmit} noValidate>
        <LoadingOverlay open={isLoading} updatingText="Loading..." />
        <FormLayoutRow columns={6}>
          <FormLayoutItem>
            <TransferNameField />
          </FormLayoutItem>
        </FormLayoutRow>
        <FormLayoutRow>
          <FormLayoutItem>
            <SimulatedProposalSelectField
              householdId={householdId}
              proposalOptions={proposalOptions}
              charitableTrustModalKind={charitableTrustModalKind}
            />
          </FormLayoutItem>
        </FormLayoutRow>
        <Stack spacing={2}>
          <Card
            variant="filled"
            sx={{ p: 2, pb: 4, borderRadius: theme.spacing(0.75) }}
          >
            <Stack>
              <Typography variant="title-l" sx={{ pb: 2 }}>
                Transfer details
              </Typography>
              <FormLayoutRow>
                <FormLayoutItem>
                  <SourceEntitySelectField
                    sourceEntityOptions={sourceEntityOptions}
                    sourceIndividualOptions={sourceIndividualOptions}
                  />
                </FormLayoutItem>
              </FormLayoutRow>
              <FormLayoutRow>
                <FormLayoutItem>
                  <FundingRadioGroupField sourceValues={sourceValues} />
                </FormLayoutItem>
              </FormLayoutRow>
              <Stack spacing={2}>
                {fundingKind === 'assets' && (
                  <SelectedAssetDetailsField sourceValues={sourceValues} />
                )}
                <FundingWarning
                  householdId={householdId}
                  waterfallId={waterfallId}
                  sourceValues={sourceValues}
                />
                <ForceLiquidationOnTransferField />
              </Stack>
            </Stack>
          </Card>
        </Stack>
      </form>
    </FormModal>
  );
}

export function EstateWaterfallHypotheticalCharitableTrustModal(
  props: EstateWaterfallHypotheticalCharitableTrustModalProps
) {
  const defaultValues = getDefaultValues({
    sourceId: props.initialSourceId,
  });

  const formMethods = useForm<HypotheticalCharitableTrustFormShape>({
    defaultValues,
  });

  return (
    <EstateWaterfallHypotheticalCharitableTrustModalContextProvider
      waterfallId={props.waterfallId}
      hcrtId={props.hcrtId}
      hcltId={props.hcltId}
      charitableTrustModalKind={props.charitableTrustModalKind}
      initialSourceId={props.initialSourceId}
    >
      <FormProvider {...formMethods}>
        <Stack spacing={2}>
          <EstateWaterfallHypotheticalCharitableTrustModalSyncer
            householdId={props.householdId}
            waterfallId={props.waterfallId}
          />
          <EstateWaterfallHypotheticalCharitableTrustModalInner {...props} />
        </Stack>
      </FormProvider>
    </EstateWaterfallHypotheticalCharitableTrustModalContextProvider>
  );
}
